import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Loading from '../components/Loading';

const MyExpenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchExpenses();
  }, []);

  useEffect(() => {
    filterExpenses();
  }, [expenses, statusFilter, searchTerm]);

  const fetchExpenses = async () => {
    try {
      const res = await axios.get('/api/expenses/my-expenses');
      setExpenses(res.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterExpenses = () => {
    let filtered = expenses;
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(e => e.status === statusFilter);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(e => 
        e.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredExpenses(filtered);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) return <Loading />;

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">My Expenses</h1>
          <p className="page-subtitle">View and manage your expense reports</p>
        </div>
        <Link to="/expenses/new" className="btn btn-primary">
          + New Expense
        </Link>
      </div>

      <div className="filters">
        <input
          type="text"
          className="form-input"
          placeholder="Search expenses..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="form-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="paid">Paid</option>
        </select>
      </div>

      <div className="card">
        <div className="card-body">
          {filteredExpenses.length === 0 ? (
            <div className="empty-state">
              <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              <h3 className="empty-state-title">
                {expenses.length === 0 ? 'No expenses yet' : 'No matching expenses'}
              </h3>
              <p className="empty-state-text">
                {expenses.length === 0 
                  ? 'Submit your first expense report to get started'
                  : 'Try adjusting your filters'}
              </p>
              {expenses.length === 0 && (
                <Link to="/expenses/new" className="btn btn-primary">Submit Expense</Link>
              )}
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Payment</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExpenses.map(expense => (
                    <tr key={expense._id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{expense.title}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                          {expense.description.substring(0, 40)}...
                        </div>
                      </td>
                      <td>{expense.category}</td>
                      <td>{formatDate(expense.expenseDate)}</td>
                      <td style={{ fontWeight: 600 }}>{formatCurrency(expense.amount)}</td>
                      <td>
                        <span className="badge" style={{ background: 'var(--gray-100)', color: 'var(--gray-600)' }}>
                          {expense.paymentType || 'reimbursement'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge badge-${expense.status}`}>
                          {expense.status}
                        </span>
                      </td>
                      <td>
                        <Link to={`/expenses/${expense._id}`} className="btn btn-outline btn-sm">
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyExpenses;
